import { Fragment, useCallback, useEffect, useState } from "react";
import { APP_LOCALE } from "../../../config/locale";
import { Modal } from "../../../components/Modal";
import { ListToolbar } from "../../../components/ListToolbar";
import { DetailsToggle } from "../../../components/DetailsToggle";
import {
  ApiError,
  createMeetingMinute,
  deleteMeetingMinute,
  getMeetingMinutes,
  updateMeetingMinute,
  type MeetingMinute,
} from "../../../atas/atasApi";

interface WorkMeetingMinutesTabProps {
  workId: number;
}

const MEETING_TYPES = ["Cliente", "Subempreiteiro", "Interna"];

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const toInputDate = (value?: string | null): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

export function WorkMeetingMinutesTab({ workId }: WorkMeetingMinutesTabProps) {
  const [minutes, setMinutes] = useState<MeetingMinute[]>([]);
  const [filter, setFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const [titulo, setTitulo] = useState("");
  const [tipo, setTipo] = useState("");
  const [data, setData] = useState("");
  const [participantes, setParticipantes] = useState("");
  const [notas, setNotas] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMinute, setEditingMinute] = useState<MeetingMinute | null>(null);

  const loadMinutes = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      setMinutes(await getMeetingMinutes(workId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar as atas de reunião.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    loadMinutes();
  }, [loadMinutes]);

  const resetForm = () => {
    setTitulo("");
    setTipo("");
    setData("");
    setParticipantes("");
    setNotas("");
  };

  const toggleExpanded = (minuteId: number) => {
    setExpandedId((prev) => (prev === minuteId ? null : minuteId));
  };

  const openCreate = () => {
    setEditingMinute(null);
    resetForm();
    setError(null);
    setIsModalOpen(true);
  };

  const openEdit = (minute: MeetingMinute) => {
    setEditingMinute(minute);
    setTitulo(minute.title ?? "");
    setTipo(minute.meetingType ?? "");
    setData(toInputDate(minute.meetingDate));
    setParticipantes(minute.participants ?? "");
    setNotas(minute.notes ?? "");
    setError(null);
    setIsModalOpen(true);
  };

  const closeForm = () => {
    setIsModalOpen(false);
    setEditingMinute(null);
    resetForm();
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!titulo.trim()) {
      setError("O assunto é obrigatório.");
      return;
    }
    setIsSaving(true);
    setError(null);
    const payload = {
      workId,
      title: titulo.trim(),
      meetingType: tipo !== "" ? tipo : null,
      meetingDate: data !== "" ? data : null,
      participants: participantes.trim() !== "" ? participantes.trim() : null,
      notes: notas.trim() !== "" ? notas.trim() : null,
    };
    try {
      if (editingMinute) {
        const updated = await updateMeetingMinute(editingMinute.id, payload);
        setMinutes((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const created = await createMeetingMinute(payload);
        setMinutes((prev) => [created, ...prev]);
      }
      closeForm();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : editingMinute
            ? "Não foi possível atualizar a ata de reunião."
            : "Não foi possível adicionar a ata de reunião.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (minuteId: number) => {
    if (deletingId !== null) return;
    if (!window.confirm("Eliminar esta ata de reunião?")) return;
    setDeletingId(minuteId);
    setError(null);
    try {
      await deleteMeetingMinute(minuteId);
      setMinutes((prev) => prev.filter((item) => item.id !== minuteId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setMinutes((prev) => prev.filter((item) => item.id !== minuteId));
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível eliminar a ata de reunião.");
        loadMinutes(true);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const normalizedFilter = filter.trim().toLowerCase();
  const shown =
    normalizedFilter === ""
      ? minutes
      : minutes.filter((minute) =>
          [minute.title ?? "", minute.meetingType ?? ""]
            .join(" ")
            .toLowerCase()
            .includes(normalizedFilter),
        );

  return (
    <div className="space-y-6">
      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar atas…"
        summary={<span className="text-secondary-text text-sm">{shown.length} ata(s)</span>}
      >
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Nova ata
        </button>
      </ListToolbar>

      {error && <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3" style={{ borderRadius: "var(--radius)" }}>{error}</div>}

      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="min-w-[720px] w-full">
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3" style={{ width: 56 }}></th>
              <th className="px-4 py-3">Assunto</th>
              <th className="px-4 py-3 w-[160px]">Tipo</th>
              <th className="px-4 py-3 w-[120px]">Data</th>
              <th className="px-4 py-3 w-[280px]">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-text">A carregar…</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-text">{filter !== "" ? "Sem resultados para o filtro." : "Sem atas de reunião."}</td></tr>
            ) : shown.map((minute) => (
              <Fragment key={minute.id}>
                <tr className="border-b border-border last:border-b-0 align-top">
                  <td className="px-4 py-3">
                    <DetailsToggle open={expandedId === minute.id} onToggle={() => toggleExpanded(minute.id)} />
                  </td>
                  <td className="px-4 py-3 whitespace-normal break-words">{minute.title || "—"}</td>
                  <td className="px-4 py-3 break-words">{minute.meetingType || "—"}</td>
                  <td className="px-4 py-3 break-words">{formatDate(minute.meetingDate)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap items-center">
                      <button className="text-primary underline" type="button" onClick={() => openEdit(minute)}>Editar</button>
                      <button className="text-destructive underline disabled:opacity-50 disabled:cursor-not-allowed" type="button" disabled={deletingId === minute.id} onClick={() => handleDelete(minute.id)}>{deletingId === minute.id ? "A eliminar…" : "Eliminar"}</button>
                    </div>
                  </td>
                </tr>
                {expandedId === minute.id && (
                  <tr className="border-b border-border last:border-b-0">
                    <td colSpan={5} className="px-4 py-3 bg-muted">
                      <div className="space-y-3 text-sm">
                        <div>
                          <div className="text-secondary-text mb-1">Participantes</div>
                          <div className="text-foreground whitespace-pre-wrap break-words">{minute.participants || "—"}</div>
                        </div>
                        <div>
                          <div className="text-secondary-text mb-1">Notas</div>
                          <div className="text-foreground whitespace-pre-wrap break-words">{minute.notes || "—"}</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <Modal
          title={editingMinute ? "Editar ata de reunião" : "Nova ata de reunião"}
          onClose={closeForm}
          footer={
            <>
              <button type="button" onClick={closeForm} className="px-4 py-2 min-h-[44px] border border-border" style={{ borderRadius: "var(--radius)" }}>Cancelar</button>
              <button type="button" disabled={isSaving} onClick={handleSave} className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed" style={{ borderRadius: "var(--radius)" }}>{isSaving ? "A guardar…" : "Guardar"}</button>
            </>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Assunto *
              <input value={titulo} onChange={(event) => setTitulo(event.target.value)} maxLength={200} placeholder="Assunto" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Tipo de Reunião
              <select value={tipo} onChange={(event) => setTipo(event.target.value)} className="px-3 py-2 border border-border rounded bg-background text-foreground">
                <option value="">Selecione um tipo</option>
                {MEETING_TYPES.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Data
              <input type="date" value={data} onChange={(event) => setData(event.target.value)} className="px-3 py-2 border border-border rounded bg-background text-foreground" />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Participantes
              <textarea rows={2} value={participantes} onChange={(event) => setParticipantes(event.target.value)} maxLength={1000} placeholder="Participantes" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
            </label>
            <label className="flex flex-col gap-1 text-sm text-secondary-text md:col-span-2">
              Notas
              <textarea rows={5} value={notas} onChange={(event) => setNotas(event.target.value)} maxLength={4000} placeholder="Notas" className="px-3 py-2 border border-border rounded bg-background text-foreground" />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}
